import { computed } from "vue";
import { POPULATION_AUTO_COLORS } from "@/lib/chartPalettes";
import { resolveLocalizedTitle } from "./useCardData";

/**
 * Bar chart data composable — builds bar and multi-population bar data
 * from the shared question data context.
 */
export function useBarChartData(ctx) {
  const {
    chartsData,
    displayLocale,
    questionMeta,
    isMatrix,
    activeItems,
    effectiveQuestionId,
    includeSkips,
    rankingTopN,
    subtotals,
    choiceColors,
    showSentiment,
    resolvedPopulations,
    getAdjustedBase,
    getCardBase,
    buildSkipEntry,
    appendSubtotalBars,
    buildBarData,
  } = ctx;

  const buildPopulationsBarData = (allRaw, questionId = null) => {
    const { showAll, pops } = resolvedPopulations.value;
    const result = [];

    // "All" population
    const allBase = includeSkips.value || rankingTopN.value ? getAdjustedBase(allRaw) : null;
    const allData = buildBarData(allRaw.answers, allBase, showSentiment.value, "all", questionId);
    if (includeSkips.value) {
      const skip = buildSkipEntry(allRaw, allBase);
      if (skip) allData.push(skip);
    }
    if (allData.length > 0 && showAll) {
      result.push({
        popId: "all",
        label: "All",
        color: POPULATION_AUTO_COLORS[0],
        data: allData,
      });
    }

    // Additional populations
    for (const pop of pops) {
      const popRaw = allRaw[pop.key];
      if (!popRaw?.answers) continue;

      const popBase = includeSkips.value || rankingTopN.value ? getAdjustedBase(popRaw) : null;
      const popData = buildBarData(popRaw.answers, popBase, showSentiment.value, pop.key, questionId);
      if (includeSkips.value) {
        const skip = buildSkipEntry(popRaw, popBase);
        if (skip) popData.push(skip);
      }

      // Align with "All" population order
      const aligned = allData.map((allItem) => {
        const match = popData.find((d) => d.code === allItem.code);
        return match || { ...allItem, value: 0, count: 0 };
      });

      result.push({
        popId: pop.id,
        label: pop.label,
        color: pop.color,
        data: aligned,
      });
    }

    const cc = choiceColors.value;
    if (cc) {
      for (const pop of result) {
        pop.data.forEach((d) => { d.color = cc[d.code] || null; });
      }
    }

    if (subtotals.value?.length) {
      for (const pop of result) {
        pop.data = appendSubtotalBars(pop.data, subtotals.value);
      }
    }

    return result;
  };

  const populationsBarData = computed(() => {
    if (!questionMeta.value) return [];
    const allRaw = chartsData.value[effectiveQuestionId.value];
    if (!allRaw) return [];
    return buildPopulationsBarData(allRaw);
  });

  // One bar chart per item when a matrix is displayed as bars
  const matrixItemsBarData = computed(() => {
    if (!isMatrix.value || !activeItems.value?.length) return [];
    return activeItems.value
      .map((item) => {
        const itemRaw = chartsData.value[item.id];
        if (!itemRaw) return null;
        return {
          itemId: item.id,
          itemLabel: resolveLocalizedTitle(item.item_title, displayLocale.value) || item.item_name,
          itemN: getAdjustedBase(itemRaw),
          populations: buildPopulationsBarData(itemRaw, item.id),
        };
      })
      .filter(Boolean);
  });

  // Per-population respondents
  const populationRespondents = computed(() => {
    if (!questionMeta.value) return [];
    const questionId = effectiveQuestionId.value;
    const allRaw = chartsData.value[questionId];
    if (!allRaw) return [];

    const { showAll, pops } = resolvedPopulations.value;
    const result = [];

    if (showAll) {
      result.push({
        token: "all",
        label: "All",
        color: POPULATION_AUTO_COLORS[0],
        respondents: getCardBase(allRaw),
      });
    }

    for (const pop of pops) {
      const popRaw = allRaw[pop.key];
      if (!popRaw) continue;
      result.push({
        token: pop.token,
        label: pop.label,
        color: pop.color,
        respondents: getCardBase(popRaw),
      });
    }

    return result;
  });

  return {
    populationsBarData,
    matrixItemsBarData,
    populationRespondents,
  };
}
